import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ShareDialog } from "../../components/ShareDialog";
import { Plus, Lock, FileDown, Trash2, Search, Share2, ShieldCheck, Ban } from "lucide-react";
import { toast } from "sonner";

export default function DevisListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [shareItem, setShareItem] = useState(null);

  const load = () => api.get("/devis").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (it.numero || "").toLowerCase().includes(q) || (it.client_nom || "").toLowerCase().includes(q) || (it.vendeur_nom || "").toLowerCase().includes(q);
  });

  const remove = async (id) => {
    try {
      await api.delete(`/devis/${id}`);
      toast.success("Devis supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Devis">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="relative w-full sm:max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="devis-search-input" placeholder="Rechercher par n°, client, vendeur..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        {hasPerm(user, "devis", "create") && (
          <Link to="/devis/new">
            <Button data-testid="devis-add-button" className="gap-2 shrink-0 w-full sm:w-auto"><Plus size={16} /> Nouveau devis</Button>
          </Link>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="devis-list">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-secondary text-left">
            <tr><th className="p-3">N°</th><th className="p-3">Date</th><th className="p-3">Client</th><th className="p-3">Statut</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`devis-row-${it.id}`}>
                <td className="p-3">
                  {it.numero}
                  {it.is_shared_to_me && (
                    <span className="block text-xs text-muted-foreground" data-testid={`devis-shared-badge-${it.id}`}>
                      Partagé par {it.shared_by_label} ({it.share_mode === "write" ? "écriture" : "lecture"})
                    </span>
                  )}
                </td>
                <td className="p-3">{it.date}</td>
                <td className="p-3">{it.client_nom}</td>
                <td className="p-3">
                  {it.annule ? (
                    <span className="inline-flex items-center gap-1 text-destructive" data-testid={`devis-status-annule-${it.id}`}><Ban size={13} />Annulé</span>
                  ) : it.status === "cloture" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`devis-status-cloture-${it.id}`}><ShieldCheck size={13} />Clôturé</span>
                  ) : (
                    it.status
                  )}
                </td>
                <td className="p-3 text-right">
                  {it.can_open ? (
                    <div className="flex justify-end items-center gap-3">
                      <Link to={`/devis/${it.id}`} data-testid={`devis-open-${it.id}`} className="text-primary text-sm">
                        {hasPerm(user, "devis", "edit") ? "Modifier" : "Voir"}
                      </Link>
                      <a href={`${api.defaults.baseURL}/devis/${it.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`devis-pdf-${it.id}`} title="PDF"><FileDown size={15} /></a>
                      {hasPerm(user, "partage_document") && !it.is_shared_to_me && (
                        <button data-testid={`devis-share-${it.id}`} onClick={() => setShareItem(it.id)} title="Partager"><Share2 size={15} /></button>
                      )}
                      {hasPerm(user, "devis", "delete") && !it.is_shared_to_me && it.status !== "cloture" && (
                        <button data-testid={`devis-delete-${it.id}`} onClick={() => remove(it.id)} title="Supprimer" className="text-destructive"><Trash2 size={15} /></button>
                      )}
                    </div>
                  ) : (
                    <Lock size={14} className="text-muted-foreground inline" data-testid={`devis-locked-${it.id}`} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShareDialog open={!!shareItem} onOpenChange={(v) => !v && setShareItem(null)} module="devis" itemId={shareItem} onShared={load} />
    </Layout>
  );
}
